/**
 * Implementa de forma GLOBAL un contador de caracteres en cualquier
 * <input>/<textarea> que tenga un atributo `maxlength` (o `limitTo`),
 * funcionando también dentro de modales y contenido renderizado dinámicamente.
 *
 * Comportamiento:
 *  - Muestra "X/max" bajo el campo.
 *  - Al alcanzar el máximo muestra en rojo "X/m · límite del campo alcanzado".
 *  - La restricción de escribir por encima del máximo la aplica el atributo nativo
 *    maxlength del propio campo (no requiere lógica extra para texto).
 *
 * Se debe llamar una única vez tras el bootstrap de la app:
 *   initCharLimitHints();
 */
export function initCharLimitHints(): void {
  const attached = new WeakSet<HTMLElement>();

  const render = (host: HTMLElement): void => {
    if (attached.has(host)) return;
    if (host.hasAttribute('data-no-limit-hint')) return;
    // No mostrar contador en campos de contraseña: el indicador se interpone con
    // el botón de mostrar/ocultar contraseña y no aporta información útil.
    if (host instanceof HTMLInputElement && host.type === 'password') return;
    // Flatpickr genera sus propios <input> numéricos (hora/minuto) dentro del
    // popup del selector, cada uno con su propio maxlength interno para
    // limitar dígitos — no son campos del formulario del usuario, así que
    // mostrarles el contador "X/2 · límite alcanzado" se ve como un mensaje
    // roto superpuesto sobre el selector. El límite de dígitos lo sigue
    // aplicando el maxlength nativo del propio flatpickr; solo se oculta el
    // aviso visual.
    if (host.closest('.flatpickr-calendar, .numInputWrapper')) return;
    const max = Number(host.getAttribute('limitTo') || host.getAttribute('maxlength') || 0);
    if (!max) return;

    let hint: HTMLElement | null = null;
    const update = (): void => {
      if (!hint) return;
      const value = host instanceof HTMLSelectElement || host instanceof HTMLTextAreaElement || host instanceof HTMLInputElement
        ? host.value
        : '';
      const len = value.length;
      const atLimit = len >= max;
      hint.textContent = `${len}/${max}${atLimit ? ' · límite del campo alcanzado' : ''}`;
      hint.classList.toggle('over', atLimit);
    };

    const ensure = (): void => {
      if (hint) return;
      hint = document.createElement('span');
      hint.className = 'limit-hint';
      host.insertAdjacentElement('afterend', hint);
      update();
    };

    host.addEventListener('input', () => {
      ensure();
      update();
    });
    host.addEventListener('focus', ensure);
    attached.add(host);
    // Render inmediato para valores ya precargados (edición).
    queueMicrotask(ensure);
  };

  const scan = (): void => {
    document.querySelectorAll<HTMLElement>('input[maxlength]:not([data-no-limit-hint]), textarea[maxlength]:not([data-no-limit-hint]), input[limitTo]:not([data-no-limit-hint]), textarea[limitTo]:not([data-no-limit-hint])').forEach(render);
  };

  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('input', (e: Event) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') && (t.hasAttribute('maxlength') || t.hasAttribute('limitTo'))) {
      render(t);
    }
  }, true);

  const observer = new MutationObserver(() => scan());
  observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
}
